import { BaseWebComponentView } from '../../../framework/BaseWebComponentView';
import { Column, ColumnKeys, COLUMNS, FILTERS, PREDEFINED_FILTERS_KEYS } from '../types';
import { WEB_COMPONENT_GRID_VIEW } from '../../../webcomponents.constants';
import { GridViewWebComponent } from '../../../grid/gridView';
import { RelayPatchCachingServersGridDataProvider } from '../dataProvider/RelayPatchCachingServersGridDataProvider';
import { RelayPatchCachingServersWebComponent } from '../component/RelayPatchCachingServersWebComponent';
import { InputWidthValues } from '../../../ui/gzInput';
import { ColumnType } from '../../../grid/types/common';

export class RelayPatchCachingServersView extends BaseWebComponentView {
  getDomElements() {
    this.dom.$wrapper = this.shadow.querySelector('.wrapper');
    this.dom.$gridView = this.shadow.querySelector(WEB_COMPONENT_GRID_VIEW) as GridViewWebComponent<Column, ColumnKeys>;
    this.dom.$searchInput = this.shadow.querySelector('#search-relay-input');
    this.dom.$customHostnamesInput = this.shadow.querySelector('#custom-relay-field');
    // used just for placeholder
    this.dom.$customHostnamesInnerInput = this.shadow.querySelector('#custom-relay-input');
    this.dom.$addRelayButton = this.shadow.querySelector('#add-relay-button');

    // TODO: call start on grid component
    if (this.dom.$gridView.injectDependencies) {
      this.dom.$gridView.injectDependencies(this.dependencies);
      this.dom.$gridView.start().then().catch(console.error);
    }
  }

  buildSuccessView(data: { component: RelayPatchCachingServersWebComponent }) {
    this.dom.$searchInput.placeholder = this.dependencies.translateFn('Relay agent -> Windows or Linux relay agent');
    this.dom.$customHostnamesInnerInput.placeholder = this.dependencies.translateFn('Custom Name/IP');
    this.dom.$searchInput.disabled = false;
    this.dom.$searchInput.readonly = false;
    this.dom.$searchInput.inputWidth = InputWidthValues.LARGE;
    this.dom.$customHostnamesInput.manualValidation = false;

    if (data.component.disabled) {
      this.dom.$searchInput.disabled = true;
      this.dom.$addRelayButton.disabled = true;
      this.dom.$customHostnamesInput.disabled = true;
    }
    this.dom.$gridView.propertiesSet = false;

    this.dom.$gridView.columnRenderers = {
      [ColumnKeys.PRIORITY]: this.renderPriority.bind(this),
      [ColumnKeys.ACTIONS]: this.renderActions.bind(this, data.component),
    };

    this.dom.$gridView.viewId = 'maintenance-windows-relay-caching-servers';
    this.dom.$gridView.dataProvider = new RelayPatchCachingServersGridDataProvider(this.dependencies, data.component);
    this.dom.$gridView.allColumnDetails = this.getColumnDetails();
    this.dom.$gridView.columns = COLUMNS;
    this.dom.$gridView.filters = FILTERS;
    this.dom.$gridView.predefinedFiltersKeys = PREDEFINED_FILTERS_KEYS;
    this.dom.$gridView.isCompactViewChecked = true;
    this.dom.$gridView.removeFilters = true;
    this.dom.$gridView.hideFooter = true;
    this.dom.$gridView.hideHeaderActions = true;

    this.dom.$gridView.propertiesSet = true;
  }

  getColumnDetails() {
    return [
      {
        column: Column.PRIORITY,
        key: ColumnKeys.PRIORITY,
        columnName: this.dependencies.translateFn('Priority'),
        type: 2,
        editingDisabled: true,
      },
      {
        column: Column.NAME,
        key: ColumnKeys.NAME,
        columnName: this.dependencies.translateFn('Relay'),
        type: 2,
        editingDisabled: true,
      },
      {
        column: Column.CUSTOMHOST,
        key: ColumnKeys.CUSTOMHOST,
        columnName: this.dependencies.translateFn('Custom Name/IP'),
        type: ColumnType.STRING,
      },
      {
        column: Column.ACTIONS,
        key: ColumnKeys.ACTIONS,
        columnName: this.dependencies.translateFn('Actions'),
        type: 2,
      },
    ];
  }

  buildErrorView(error: Error) {
    super.buildErrorView(error);
  }

  private renderPriority(rowData) {
    const priority = rowData.index + 1;
    return priority;
  }

  renderActions(component, rowData) {
    const container = document.createElement('actions');

    let upDocumentFragment;
    let downDocumentFragment;
    let deleteDocumentFragment;

    deleteDocumentFragment = this.createIconButton(
      'delete-relay-button',
      'ds-icon-clear',
      'secondary',
      '20',
      'round',
      'quiet',
      '24'
    );
    const $deleteButton = deleteDocumentFragment.querySelector('#delete-relay-button');
    $deleteButton.setAttribute('disabled', 'disabled');

    if (!component.disabled) {
      $deleteButton.removeAttribute('disabled');
      $deleteButton?.addEventListener('click', () => {
        const event = new CustomEvent('deleteRelay', { detail: { rowData } });
        this.dom.$gridView.dispatchEvent(event);
      });
    }

    // if only one row, can't go anywhere
    if (component.cachingServers.length !== 1) {
      // if first, can't go up
      if (rowData.index !== 0) {
        upDocumentFragment = this.createIconButton(
          'up-relay-button',
          'ds-icon-arrow_up_collapse',
          'secondary',
          '20',
          'round',
          'quiet',
          '24'
        );
        const $upButton = upDocumentFragment.querySelector('#up-relay-button');
        $upButton.setAttribute('disabled', 'disabled');

        if (!component.disabled) {
          $upButton.removeAttribute('disabled');
          $upButton?.addEventListener('click', () => {
            const event = new CustomEvent('upRelay', { detail: { rowData } });
            this.dom.$gridView.dispatchEvent(event);
          });
        }
      }

      // if last, can't go down
      if (rowData.index !== component.cachingServers.length - 1) {
        downDocumentFragment = this.createIconButton(
          'down-relay-button',
          'ds-icon-arrow_down_expand',
          'secondary',
          '20',
          'round',
          'quiet',
          '24'
        );
        const $downButton = downDocumentFragment.querySelector('#down-relay-button');
        $downButton.setAttribute('disabled', 'disabled');

        if (!component.disabled) {
          $downButton.removeAttribute('disabled');
          $downButton?.addEventListener('click', () => {
            const event = new CustomEvent('downRelay', { detail: { rowData } });
            this.dom.$gridView.dispatchEvent(event);
          });
        }
      }
    }

    container.append(deleteDocumentFragment);
    if (upDocumentFragment) container.append(upDocumentFragment);
    if (downDocumentFragment) container.append(downDocumentFragment);

    return container;
  }

  private createTemplateElement(html: string): HTMLTemplateElement {
    const templateElement = document.createElement('template');
    templateElement.innerHTML = html;

    return templateElement;
  }

  private createIconButton(id, icon, buttoncolor, fontsize, buttontype, buttonfill, size) {
    return this.createTemplateElement(`<gz-icon-button
      id = "${id}"
      icon="${icon}"
      buttoncolor="${buttoncolor}"
      fontsize="${fontsize}"
      buttontype="${buttontype}"
      buttonfill="${buttonfill}"
      size="${size}"
    ></gz-icon-button>`).content;
  }
}
